import React, { useState, useEffect } from "react";
import { Modal, Checkbox, Button, message } from "antd";
import { Download, Eye, FileText, FileSpreadsheet, Image } from "lucide-react";
import { TInventory } from "~/types/inventory";
import { InventoryStatusCode } from "~/utils/constants";
import { capitalize, formatDimensions } from "~/utils";

export type ExportType = "csv" | "pdf" | "photo-list";

interface ExportColumn {
  key: string;
  label: string;
  enabled: boolean;
}

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  exportType: ExportType;
  inventories: TInventory[];
  onExport: (columns: string[], exportType: ExportType, action: "view" | "download") => void;
}

const defaultColumns: ExportColumn[] = [
  { key: "inventoryRef", label: "Ref #", enabled: true },
  { key: "clientInternalRef", label: "Client Ref #", enabled: true },
  { key: "title", label: "Title", enabled: true },
  { key: "artistName", label: "Artist", enabled: true },
  { key: "medium", label: "Medium", enabled: true },
  { key: "dimensions", label: "Dimensions", enabled: true },
  { key: "packingType", label: "Packing Type", enabled: true },
  { key: "status", label: "Status", enabled: true },
  { key: "location", label: "Location", enabled: true },
  { key: "value", label: "Value", enabled: false },
  { key: "weight", label: "Weight", enabled: false },
  { key: "description", label: "Description", enabled: false },
  { key: "notes", label: "Notes", enabled: false },
  { key: "pieceCount", label: "Piece Count", enabled: false },
  { key: "packageCount", label: "Package Count", enabled: false },
];

export const ExportModal: React.FC<ExportModalProps> = ({
  open,
  onClose,
  exportType,
  inventories,
  onExport,
}) => {
  const [columns, setColumns] = useState<ExportColumn[]>(defaultColumns);

  useEffect(() => {
    // Reset to defaults when modal opens
    if (open) {
      setColumns(defaultColumns);
    }
  }, [open]);

  const toggleColumn = (key: string) => {
    setColumns((prev) =>
      prev.map((col) => (col.key === key ? { ...col, enabled: !col.enabled } : col))
    );
  };

  const selectAll = () => {
    setColumns((prev) => prev.map((col) => ({ ...col, enabled: true })));
  };

  const deselectAll = () => {
    setColumns((prev) => prev.map((col) => ({ ...col, enabled: false })));
  };

  const handleExport = (action: "view" | "download") => {
    const selectedColumns = columns.filter((col) => col.enabled).map((col) => col.key);
    
    if (selectedColumns.length === 0) {
      message.warning("Please select at least one column to export.");
      return;
    }

    onExport(selectedColumns, exportType, action);
  };

  const getExportTypeLabel = () => {
    switch (exportType) {
      case "csv":
        return "CSV";
      case "pdf":
        return "PDF";
      case "photo-list":
        return "Photo List";
      default:
        return "Export";
    }
  };

  const getExportTypeIcon = () => {
    switch (exportType) {
      case "csv":
        return <FileSpreadsheet size={20} />;
      case "pdf":
        return <FileText size={20} />;
      case "photo-list":
        return <Image size={20} />;
      default:
        return null;
    }
  };

  const selectedCount = columns.filter((col) => col.enabled).length;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={
        <div className="flex items-center gap-2">
          {getExportTypeIcon()}
          <span className="font-bold uppercase tracking-wide">
            Export {getExportTypeLabel()} - Select Columns
          </span>
        </div>
      }
      width={600}
      className="export-modal"
    >
      <div className="space-y-4">
        <div className="p-3 bg-gray-50 border-2 border-black">
          <p className="text-sm font-medium text-black">
            {inventories.length} item{inventories.length !== 1 ? "s" : ""} will be exported
          </p>
        </div>

        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-black">
            Select columns ({selectedCount} selected)
          </span>
          <div className="flex gap-2">
            <Button size="small" onClick={selectAll} className="text-xs">
              Select All
            </Button>
            <Button size="small" onClick={deselectAll} className="text-xs">
              Deselect All
            </Button>
          </div>
        </div>

        <div className="border-2 border-black max-h-96 overflow-y-auto p-4 bg-[#fffef0]">
          <div className="grid grid-cols-2 gap-3">
            {columns.map((column) => (
              <div key={column.key} className="flex items-center">
                <Checkbox
                  checked={column.enabled}
                  onChange={() => toggleColumn(column.key)}
                  className="text-sm"
                >
                  <span className="text-sm">{column.label}</span>
                </Checkbox>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t-2 border-black">
          <Button onClick={onClose} className="border-2 border-black">
            Cancel
          </Button>
          <Button
            onClick={() => handleExport("view")}
            className="border-2 border-black bg-[#fffef0] text-black font-bold uppercase tracking-wide flex items-center gap-2"
          >
            <Eye size={16} />
            View in New Tab
          </Button>
          <Button
            onClick={() => handleExport("download")}
            type="primary"
            className="border-2 border-black bg-black text-white font-bold uppercase tracking-wide flex items-center gap-2"
          >
            <Download size={16} />
            Download
          </Button>
        </div>
      </div>
    </Modal>
  );
};


